import { Dispatch, useEffect, useState } from "react";
import { useMediaQuery } from "@react-hookz/web";
import { AnimatePresence, motion } from "framer-motion";
import MobileMixModeButton from "../molecules/MobileMixModeButton";
import MultiMixModeScreen from "../organisms/MultiMixModeScreen";
import SingleTrackMixModeScreen from "../organisms/SingleTrackMixModeScreen";
import { sideAnimation } from "../constants";
import { State } from "../state/reducer";
import Action from "../state/actions";
import * as GameLayout from "ui/templates/Game/LayoutComponents";
import ReferenceOverlay from "../molecules/ReferenceOverlay";
import { isReferenceMode, isReportCardMode } from "../state/selectors";
import classNames from "classnames";
import { useAudioContextReady } from "util/audioContext";

interface Props {
  state: State;
  dispatch: Dispatch<Action>;
  mobileToolbarStyle: GameLayout.MobileToolbarStyle;
}

export default function MixPuzzleTemplate({
  state,
  dispatch,
  mobileToolbarStyle,
}: Props) {
  const isMobile = useMediaQuery("(max-width: 675px)");
  const isMultiMixMode = isMobile && state.multiMixMode;
  const [audioCtxReady] = useAudioContextReady();
  const showMobileFloatingToolbar = (() => {
    if (isReportCardMode(state)) {
      if (isMultiMixMode) {
        return false;
      }

      return true;
    }

    if (!audioCtxReady) {
      return false;
    }

    return !isReferenceMode(state);
  })();

  useEffect(() => scrollTo(0, 0), [isMultiMixMode]);

  return (
    <>
      {showMobileFloatingToolbar && (
        <GameLayout.MobileFloatingToolbar
          toolbarMobileStyle={mobileToolbarStyle}
          onClick={() =>
            dispatch({ type: "updateMultiMixMode", payload: "toggle" })
          }
        >
          <MobileMixModeButton multiMixMode={state.multiMixMode} />
        </GameLayout.MobileFloatingToolbar>
      )}

      <GameLayout.Container>
        <div
          className={classNames(
            !isReferenceMode(state) && "overflow-hidden md:overflow-visible"
          )}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {isMultiMixMode ? (
              <motion.div {...sideAnimation} key="m">
                <MultiMixModeScreen
                  dispatch={dispatch}
                  state={state}
                  back={() =>
                    dispatch({ type: "updateMultiMixMode", payload: false })
                  }
                />
              </motion.div>
            ) : (
              <motion.div {...sideAnimation} key="s">
                <SingleTrackMixModeScreen dispatch={dispatch} state={state} />
              </motion.div>
            )}

            {isReferenceMode(state) && (
              <motion.div
                transition={{ duration: 0.1 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key="ref"
                onClick={() => dispatch({ type: "togglePlaybackState" })}
              >
                <ReferenceOverlay />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </GameLayout.Container>
    </>
  );
}
